import React, { FC, useRef, useState } from "react";
import { Typography, Box, Card, CardContent } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { StoryNavigationNode } from "common/build/api-parameters/story";
import { isAutoContinueDecisionPoint } from "common/build/utils/branchedStoryUtils";
import { FacilitatorStoryPartType } from "../../../../models/modelTypes";
import { StoryPartAnnotation } from "../../../session/prepareSession/StoryPartAnnotation";

const useStyles = makeStyles(() =>
    createStyles({
        decisionPointCard: {
            marginBottom: "12px",
            backgroundColor: "#EBF7F3",
            border: "1px solid #1FAF8C",
        },
        branchOption: {
            backgroundColor: "#ffffff",
            borderRadius: "4px",
            padding: "12px",
            marginBottom: "8px",
            border: "1px solid #e0e0e0",
            display: "flex",
            alignItems: "center",
            gap: "8px",
        },
        branchLabel: {
            backgroundColor: "#1FAF8C",
            color: "#ffffff",
            borderRadius: "4px",
            padding: "4px 8px",
            fontSize: "12px",
            fontWeight: "bold",
            minWidth: "24px",
            textAlign: "center",
        },
        sectionTitle: {
            marginTop: "16px",
            marginBottom: "12px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: "8px",
        },
    }),
);

interface BranchedStoryAnnotationViewProps {
    parts: StoryNavigationNode[];
    facilitatorParts: FacilitatorStoryPartType[];
    content: Map<number, string>;
    setContent: (id: number, content: string) => void;
    setIsEdited: (edited: boolean) => void;
    setIsEditorLoaded: (loaded: boolean) => void;
    isEditorLoaded: boolean;
    isQuestionsOpen: Map<number, boolean>;
    setIsQuestionsOpen: (isQuestionsOpen: Map<number, boolean>) => void;
    showQuestionsRef?: React.RefObject<HTMLButtonElement>;
    hideQuestionsRef?: React.RefObject<HTMLButtonElement>;
    tooltipOpen?: boolean;
    tourStateOpen?: boolean;
}

export const BranchedStoryAnnotationView: FC<BranchedStoryAnnotationViewProps> = ({
    parts,
    facilitatorParts,
    content,
    setContent,
    setIsEdited,
    setIsEditorLoaded,
    isEditorLoaded,
    isQuestionsOpen,
    setIsQuestionsOpen,
    showQuestionsRef,
    hideQuestionsRef,
    tooltipOpen,
    tourStateOpen,
}) => {
    const classes = useStyles();

    const partRefs = useRef<Record<number, HTMLDivElement | null>>({});

    // State management: all accordions expanded by default, all questions hidden by default
    const [accordionOpen, setAccordionOpen] = useState<Map<number, boolean>>(() => {
        const initialState = new Map<number, boolean>();
        parts.forEach((part) => {
            initialState.set(part.partNumber, true);
        });
        return initialState;
    });

    return (
        <Box>
            {parts.map((part) => {
                // Find corresponding facilitator part for this story part
                const facilitatorPart = facilitatorParts.find((fp) => fp.MappedStoryPart?.part === part.partNumber);

                // Use facilitatorPart.html if available, otherwise fall back to part.content
                const initialContent = facilitatorPart?.html || part.content;

                // Get the database ID for the MappedStoryPart (required for saving annotations)
                const mappedStoryPartId = facilitatorPart?.mappedStoryPartId || 0;

                const realDecisionPoints = (part.decisionPoints || []).filter((dp) => !isAutoContinueDecisionPoint(dp));

                const decisionPointsContent =
                    realDecisionPoints.length > 0 ? (
                        <Box mt={2} ml={2}>
                            <Typography variant="h6" className={classes.sectionTitle}>
                                <AccountTreeIcon />
                                Decision Points
                            </Typography>
                            {realDecisionPoints.map((dp, dpIndex) => (
                                <Card key={dpIndex} className={classes.decisionPointCard}>
                                    <CardContent style={{ padding: "12px" }}>
                                        <Typography
                                            variant="subtitle2"
                                            style={{ marginBottom: "12px", fontWeight: "bold" }}
                                        >
                                            {dp.decisionText}
                                        </Typography>
                                        {dp.branches.map((branch, branchIndex) => (
                                            <Box key={branchIndex} className={classes.branchOption}>
                                                <span className={classes.branchLabel}>{branch.branchLabel}</span>
                                                <Typography variant="body2" style={{ flex: 1 }}>
                                                    {branch.choiceText}
                                                </Typography>
                                                <Box display="flex" alignItems="center" style={{ gap: "4px" }}>
                                                    <ArrowForwardIcon fontSize="small" />
                                                    <Typography variant="caption">
                                                        Part {branch.targetPartNumber}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    ) : null;

                return (
                    <StoryPartAnnotation
                        key={part.partNumber}
                        partNumber={part.partNumber}
                        totalParts={parts.length}
                        isExpanded={!!accordionOpen.get(part.partNumber)}
                        onToggleExpand={(expanded) =>
                            setAccordionOpen(new Map(accordionOpen.set(part.partNumber, expanded)))
                        }
                        isQuestionsOpen={mappedStoryPartId > 0 && !!isQuestionsOpen.get(mappedStoryPartId)}
                        onToggleQuestions={(open) => {
                            if (mappedStoryPartId > 0) {
                                setIsQuestionsOpen(new Map(isQuestionsOpen.set(mappedStoryPartId, open)));
                            }
                        }}
                        initialContent={initialContent}
                        mappedStoryPartId={mappedStoryPartId}
                        content={content}
                        setContent={setContent}
                        setIsEdited={setIsEdited}
                        setIsEditorLoaded={setIsEditorLoaded}
                        isEditorLoaded={isEditorLoaded}
                        mappedStoryPart={facilitatorPart?.MappedStoryPart}
                        mappedStoryId={facilitatorPart?.MappedStoryPart?.storyId ?? undefined}
                        isMobile={false} // Branched view currently assumes non-mobile layout for annotation
                        isEndPoint={part.isEndNode}
                        extraContent={decisionPointsContent}
                        isFirstPart={part.partNumber === 1}
                        showQuestionsRef={part.partNumber === 1 ? showQuestionsRef : undefined}
                        hideQuestionsRef={part.partNumber === 1 ? hideQuestionsRef : undefined}
                        tooltipOpen={tooltipOpen}
                        tourStateOpen={tourStateOpen}
                        onBackToTop={() => {
                            setAccordionOpen(new Map(accordionOpen.set(part.partNumber, true)));
                            partRefs.current[part.partNumber]?.scrollIntoView({ behavior: "smooth" });
                        }}
                        accordionRef={(el) => {
                            partRefs.current[part.partNumber] = el;
                        }}
                    />
                );
            })}
        </Box>
    );
};

BranchedStoryAnnotationView.displayName = "BranchedStoryAnnotationView";
